import { useRef, useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import styles from "./ImageCarousel.module.css";

interface ImageCarouselProps {
  images: string[];
  title: string;
  subtitle?: string;
}

export function ImageCarousel({ images, title, subtitle }: ImageCarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [updateArrows]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight") setLightboxIndex((i) => (i === null ? null : (i + 1) % images.length));
      if (e.key === "ArrowLeft") setLightboxIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, images.length]);

  const scroll = (dir: "left" | "right") => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector(`.${styles.slide}`) as HTMLElement | null;
    const amount = card ? card.offsetWidth + 20 : 320;
    el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  return (
    <>
      <section className={styles.section}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>

        <div className={styles.outer}>
          <button
            className={`${styles.arrow} ${styles.arrowLeft} ${!canLeft ? styles.arrowHidden : ""}`}
            onClick={() => scroll("left")}
            aria-label="Previous"
          >
            <ChevronLeft size={22} />
          </button>

          <div className={styles.track} ref={trackRef}>
            {images.map((src, i) => (
              <div
                key={i}
                className={styles.slide}
                onClick={() => setLightboxIndex(i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setLightboxIndex(i)}
                aria-label="View larger"
              >
                <img src={src} alt="" className={styles.img} loading="lazy" />
              </div>
            ))}
          </div>

          <button
            className={`${styles.arrow} ${styles.arrowRight} ${!canRight ? styles.arrowHidden : ""}`}
            onClick={() => scroll("right")}
            aria-label="Next"
          >
            <ChevronRight size={22} />
          </button>
        </div>
      </section>

      {lightboxIndex !== null && (
        <div
          className={styles.lightboxBackdrop}
          onClick={() => setLightboxIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            className={styles.lightboxClose}
            onClick={() => setLightboxIndex(null)}
            aria-label="Close"
          >
            <X size={24} />
          </button>

          <button
            className={`${styles.lightboxNav} ${styles.lightboxNavLeft}`}
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + images.length) % images.length); }}
            aria-label="Previous image"
          >
            <ChevronLeft size={28} />
          </button>

          <img
            src={images[lightboxIndex]}
            alt=""
            className={styles.lightboxImg}
            onClick={(e) => e.stopPropagation()}
          />

          <button
            className={`${styles.lightboxNav} ${styles.lightboxNavRight}`}
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % images.length); }}
            aria-label="Next image"
          >
            <ChevronRight size={28} />
          </button>

          <span className={styles.lightboxCounter}>
            {lightboxIndex + 1} / {images.length}
          </span>
        </div>
      )}
    </>
  );
}
